import { PeriodType } from '@/common/Graph/types';
import Analytics from '@/system/Analytics';
import { TournamentsEvents } from '@/system/Analytics/events/TournamentsEvents';
import dayjs from 'dayjs';
import { API } from '../Client';
import { PortfolioPeriodType } from '../TradingAccount/types';
import { Tournament, TournamentPickStokDto } from './types';

namespace TournamentsService {
  /**
   * Retrieves a list of all tournaments, sorted by end date in descending order.
   *
   * @returns A promise that resolves to an array of Tournament objects.
   * @throws An error if there is an issue fetching the tournaments.
   */
  export async function getTournaments(): Promise<Tournament[]> {
    try {
      const res = await API.get<Tournament[]>('/games/tournaments');
      return (
        res?.data?.sort((a, b) => {
          if (a?.endDate && b?.endDate) {
            return dayjs(b.endDate).diff(dayjs(a.endDate));
          }
          return 0;
        }) || []
      );
    } catch (e: any) {
      throw new Error('Error fetching tournaments:', e?.message);
    }
  }

  /**
   * Retrieves a single tournament by its id.
   *
   * @param {string} id The id of the tournament to retrieve.
   * @returns A promise that resolves to a Tournament object if successful, otherwise throws an error.
   * @throws An error if there is an issue fetching the tournament.
   */
  export async function getTournament(id: string | null | undefined): Promise<Tournament> {
    try {
      if (!id) throw new Error('Tournament id is required');
      const res = await API.get<Tournament>(`/games/tournaments/${id}`);
      return res?.data;
    } catch (e: any) {
      throw new Error('Error fetching tournaments:', e?.message);
    }
  }

  /**
   * Retrieves the graph data for a tournament.
   *
   * @param {string} id The id of the tournament to retrieve graph data for.
   * @returns A promise that resolves to an object containing the graph data if successful, otherwise throws an error.
   * @throws An error if there is an issue fetching the graph data.
   */
  export async function getTournamentGraph(
    id: string,
    params?: { range?: PeriodType | PortfolioPeriodType }
  ) {
    try {
      const res = await API.get(`/games/tournaments/${id}/graph`, {
        params,
      });
      return res?.data || {};
    } catch (e: any) {
      throw new Error('Error fetching tournaments:', e?.message);
    }
  }

  /**
   * Retrieves the portfolio data for a tournament.
   *
   * @param {string} id The id of the tournament to retrieve portfolio data for.
   * @param {any} account The account object to use when retrieving the portfolio data.
   * @returns A promise that resolves to an object containing the portfolio data if successful, otherwise throws an error.
   * @throws An error if there is an issue fetching the portfolio data.
   */
  export async function getTournamentPortfolio(id: string, account: any) {
    try {
      const res = await API.get(`/games/tournaments/${id}/portfolio?userId=${account.id}`);
      return res?.data || {};
    } catch (e: any) {
      throw new Error('Error fetching tournaments:', e?.message);
    }
  }

  /**
   * Retrieves the leaderboard data for a tournament.
   *
   * @param {string} id The id of the tournament to retrieve leaderboard data for.
   * @returns A promise that resolves to an object containing the leaderboard data if successful, otherwise throws an error.
   * @throws An error if there is an issue fetching the leaderboard data.
   */
  export async function getTournamentLeaderboard(id: string) {
    try {
      const res = await API.get(`/games/tournaments/${id}/leaderboard`);
      return res?.data || {};
    } catch (e: any) {
      throw new Error('Error fetching tournaments:', e?.message);
    }
  }

  export async function addStockToPortfolio(tournamentId: string, stocks: TournamentPickStokDto[]) {
    try {
      const res = await API.post<Tournament>(
        `/games/tournaments/${tournamentId}/portfolio`,
        stocks
      );
      return res?.data || {};
    } catch (e: any) {
      if (e.response) {
        const message = e?.response?.data?.message ?? e.message
        throw new Error(message);
      } else {
        throw new Error(e.message);
      }
    }
  }

  export async function joinTournament(tournamentId: string) {
    try {
      const res = await API.post<Tournament>(`/games/tournaments/${tournamentId}/participants`);
      return res?.data || {};
    } catch (e: any) {
      if (e.response) {
        const message = `Error joining tournament: ${e?.response?.data?.message ?? e.message}`
        Analytics.track(TournamentsEvents.CLICK_JOIN_ERROR, { tournamentId, error: message });
        throw new Error(message);
      } else {
        throw new Error(e.message);
      }
    }
  }
}

export default TournamentsService;
